import React, { useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  Stack,
  Grid,
} from "@mui/material";
import type { DogFormData } from "../../types";
import { Sex, Reproduction } from "@/helpers/enums";
import { BreedAutocomplete } from "@/components/quiz/form/BreedAutocomplete";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  form: Partial<DogFormData>;
  onChange: (updates: Partial<DogFormData>) => void;
  errors?: Record<string, string>;
}

// Age preset options
const AGE_OPTIONS = [
  { key: "puppy", label: "Puppy", y: 0, m: 6 },
  { key: "adult", label: "Adult", y: 3, m: 0 },
  { key: "senior", label: "Senior", y: 8, m: 0 },
] as const;

// Shared widths (centered)
const MAX_W = 560;

// Quiz-like input styling (mirrors BreedQuestion style)
const inputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: QUIZ.color.warmWhite,
    borderRadius: "12px",
    "& fieldset": { borderColor: "rgba(74,48,38,0.25)" },
    "&:hover fieldset": { borderColor: "rgba(74,48,38,0.45)" },
    "&.Mui-focused fieldset": { borderColor: QUIZ.color.brown },

    "& input": {
      padding: { xs: "18px 16px", sm: "22px 16px" },
      fontSize: { xs: "16px", sm: "16px" },
      fontWeight: 800,
      color: QUIZ.color.brown,
      backgroundColor: QUIZ.color.warmWhite,
      textAlign: "center",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(74,48,38,0.7)",
    fontWeight: 700,
    "&.Mui-focused": { color: QUIZ.color.brown },
  },
  "& .MuiFormHelperText-root": {
    textAlign: "center",
    fontWeight: 600,
  },
};

const sectionTitleSx = {
  fontSize: 14,
  fontWeight: 900,
  color: QUIZ.color.brown,
  textAlign: "center",
  mb: 1,
};

const cardSx = {
  width: "100%",
  maxWidth: MAX_W,
  mx: "auto",
};

const choiceRowSx = {
  display: "flex",
  gap: 1.5,
  width: "100%",
  justifyContent: "center",
};

function ChoiceCard({
  label,
  emoji,
  selected,
  onClick,
  tint = QUIZ.color.cream2,
  border = "rgba(74,48,38,0.22)",
  selectedBorder = QUIZ.color.brown,
}: {
  label: string;
  emoji?: string;
  selected: boolean;
  onClick: () => void;
  tint?: string;
  border?: string;
  selectedBorder?: string;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
      sx={{
        flex: 1,
        maxWidth: 260,
        py: 2,
        px: 2,
        borderRadius: "16px",
        backgroundColor: selected ? tint : QUIZ.color.white,
        border: `2px solid ${selected ? selectedBorder : border}`,
        cursor: "pointer",
        transition: "all 0.18s ease",
        textAlign: "center",
        userSelect: "none",
        boxShadow: selected ? "0 10px 24px rgba(0,0,0,0.07)" : "0 2px 8px rgba(0,0,0,0.03)",
        "&:hover": {
          transform: "translateY(-1px)",
          borderColor: selectedBorder,
        },
      }}
    >
      {emoji ? (
        <Typography sx={{ fontSize: 28, mb: 0.5, lineHeight: 1 }}>
          {emoji}
        </Typography>
      ) : null}
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 900,
          color: selected ? QUIZ.color.brown : "rgba(74,48,38,0.85)",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export function BasicsStep({ form, onChange, errors = {} }: Props) {
  const showReproduction = form.sex !== undefined;

  const years = Number.isFinite(form.ageYears as number) ? (form.ageYears as number) : 1;
  const months = Number.isFinite(form.ageMonths as number) ? (form.ageMonths as number) : 0;

  const ageCategory = useMemo(() => {
    const totalMonths = years * 12 + months;
    return totalMonths < 12 ? "puppy" : totalMonths < 96 ? "adult" : "senior";
  }, [years, months]);

  const handleAgePreset = (y: number, m: number) => {
    onChange({ ageYears: y, ageMonths: m });
  };

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      {/* Name */}
      <Box sx={cardSx}>
        <Typography sx={sectionTitleSx}>
          What’s your dog’s name? <span style={{ color: QUIZ.color.brown }}>*</span>
        </Typography>
        <TextField
          value={form.name || ""}
          onChange={(e) => onChange({ name: e.target.value })}
          fullWidth
          placeholder="e.g. Max, Bella, Charlie…"
          error={!!errors.name}
          helperText={errors.name}
          inputProps={{
            autoCapitalize: "words",
            autoCorrect: "off",
          }}
          sx={inputSx}
        />
      </Box>

      {/* Breed */}
      <Box sx={cardSx}>
        <Typography sx={sectionTitleSx}>
          What breed is {form.name || "your dog"}?{" "}
          <span style={{ color: QUIZ.color.brown }}>*</span>
        </Typography>

        <BreedAutocomplete
          value={form.breed || ""}
          onChange={(breed) => onChange({ breed })}
          dogName={form.name}
        />
        {errors.breed && (
          <Typography sx={{ color: "#d32f2f", fontSize: 12, fontWeight: 600, textAlign: "center", mt: 0.5 }}>
            {errors.breed}
          </Typography>
        )}
      </Box>

      {/* Sex */}
      <Box sx={cardSx}>
        <Typography sx={sectionTitleSx}>
          Is {form.name || "your dog"} a boy or girl?
        </Typography>

        <Box sx={choiceRowSx}>
          <ChoiceCard
            label="Boy"
            emoji="♂"
            selected={form.sex === Sex.MALE}
            onClick={() => onChange({ sex: Sex.MALE, reproduction: undefined })}
            tint="rgba(26,87,211,0.10)"
            selectedBorder="#1A57D3"
          />
          <ChoiceCard
            label="Girl"
            emoji="♀"
            selected={form.sex === Sex.FEMALE}
            onClick={() => onChange({ sex: Sex.FEMALE, reproduction: undefined })}
            tint="rgba(209,76,63,0.10)"
            selectedBorder="#D14C3F"
          />
        </Box>
      </Box>

      {/* Reproduction */}
      {showReproduction && (
        <Box sx={cardSx}>
          <Typography sx={sectionTitleSx}>
            Is{" "}
            {form.name || "your dog"}{" "}
            {form.sex === Sex.MALE ? "neutered" : "spayed"}?
          </Typography>

          <Box sx={choiceRowSx}>
            <ChoiceCard
              label="Yes"
              selected={
                form.reproduction ===
                (form.sex === Sex.MALE ? Reproduction.NEUTERED : Reproduction.SPAYED)
              }
              onClick={() =>
                onChange({
                  reproduction:
                    form.sex === Sex.MALE ? Reproduction.NEUTERED : Reproduction.SPAYED,
                })
              }
              tint="rgba(46,125,50,0.10)"
              selectedBorder="#2E7D32"
              emoji="✓"
            />

            <ChoiceCard
              label="No"
              selected={
                form.reproduction ===
                (form.sex === Sex.MALE ? Reproduction.NOT_NEUTERED : Reproduction.NOT_SPAYED)
              }
              onClick={() =>
                onChange({
                  reproduction:
                    form.sex === Sex.MALE
                      ? Reproduction.NOT_NEUTERED
                      : Reproduction.NOT_SPAYED,
                })
              }
              tint="rgba(74, 48, 38,0.14)"
              selectedBorder={QUIZ.color.brown}
              emoji="✗"
            />
          </Box>
        </Box>
      )}

      {/* Age */}
      <Box sx={cardSx}>
        <Typography sx={sectionTitleSx}>
          How old is {form.name || "your dog"}?
        </Typography>

        {/* Presets */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            flexWrap: "wrap",
            mb: 1.5,
          }}
        >
          {AGE_OPTIONS.map((opt) => {
            const selected = ageCategory === opt.key;
            return (
              <Box
                key={opt.key}
                role="button"
                tabIndex={0}
                onClick={() => handleAgePreset(opt.y, opt.m)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleAgePreset(opt.y, opt.m);
                }}
                sx={{
                  px: 1.75,
                  py: 0.85,
                  borderRadius: "999px",
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: "pointer",
                  userSelect: "none",
                  bgcolor: selected ? "rgba(74, 48, 38,0.16)" : QUIZ.color.white,
                  border: "2px solid",
                  borderColor: selected ? QUIZ.color.brown : "rgba(74,48,38,0.20)",
                  color: selected ? QUIZ.color.brown : "rgba(74,48,38,0.75)",
                  transition: "all 0.18s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    borderColor: QUIZ.color.brown,
                    bgcolor: selected ? "rgba(74, 48, 38,0.18)" : "rgba(255,225,221,0.35)",
                  },
                }}
              >
                {opt.label}
              </Box>
            );
          })}
        </Box>

        {/* Inputs */}
        <Grid
          container
          spacing={2}
          sx={{ width: "100%", justifyContent: "center" }}
        >
          <Grid size={{xs: 12, sm: 6}}>
            <TextField
              type="number"
              placeholder="Years"
              fullWidth
              value={years}
              onChange={(e) => {
                const val = Math.max(0, Math.min(20, Number(e.target.value) || 0));
                onChange({ ageYears: val });
              }}
              inputProps={{ min: 0, max: 20 }}
              sx={{
                ...inputSx,
                "& .MuiOutlinedInput-root input": {
                  ...(inputSx as any)["& .MuiOutlinedInput-root"]["& input"],
                  textAlign: "center",
                },
              }}
            />
          </Grid>

          <Grid size={{xs: 12, sm: 6}}>
            <TextField
              type="number"
              placeholder="Months"
              fullWidth
              value={months}
              onChange={(e) => {
                const val = Math.max(0, Math.min(11, Number(e.target.value) || 0));
                onChange({ ageMonths: val });
              }}
              inputProps={{ min: 0, max: 11 }}
              sx={{
                ...inputSx,
                "& .MuiOutlinedInput-root input": {
                  ...(inputSx as any)["& .MuiOutlinedInput-root"]["& input"],
                  textAlign: "center",
                },
              }}
            />
          </Grid>
        </Grid>

        <Typography
          sx={{
            mt: 1.5,
            textAlign: "center",
            fontSize: 12.5,
            fontWeight: 700,
            color: "rgba(74,48,38,0.65)",
          }}
        >
          Tip: use “Puppy / Adult / Senior” to autofill quickly.
        </Typography>
      </Box>
    </Stack>
  );
}